import type { ReactNode } from "react";
import { useState } from "react";

interface EditorTabsProps {
  edit: ReactNode;
  preview: ReactNode;
}

type Tab = "edit" | "preview";

const TAB_BTN_BASE =
  "relative flex-1 px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none";

const TAB_INDICATOR =
  "absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-[var(--tg-link)]";

export function EditorTabs({ edit, preview }: EditorTabsProps) {
  const [active, setActive] = useState<Tab>("edit");

  return (
    <div className="flex h-full flex-col">
      <div
        role="tablist"
        aria-label="Editor or preview"
        className="flex border-b border-[var(--tg-secondary-bg)] bg-[var(--tg-bg)]"
      >
        <button
          type="button"
          role="tab"
          aria-selected={active === "edit"}
          className={`${TAB_BTN_BASE} ${active === "edit" ? "text-[var(--tg-link)]" : "text-[var(--tg-hint)] hover:text-[var(--tg-text)]"}`}
          onClick={() => {
            setActive("edit");
          }}
        >
          Edit
          {active === "edit" && <span className={TAB_INDICATOR} />}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={active === "preview"}
          className={`${TAB_BTN_BASE} ${active === "preview" ? "text-[var(--tg-link)]" : "text-[var(--tg-hint)] hover:text-[var(--tg-text)]"}`}
          onClick={() => {
            setActive("preview");
          }}
        >
          Preview
          {active === "preview" && <span className={TAB_INDICATOR} />}
        </button>
      </div>
      {/*
        Both panels stay mounted at all times — Lexical state is held inside
        the editor instance, so unmounting on tab switch would lose history
        and selection. We just toggle visibility.
      */}
      <div className="flex-1 overflow-hidden">
        <div className={active === "edit" ? "h-full" : "hidden"}>{edit}</div>
        <div className={active === "preview" ? "h-full" : "hidden"}>
          {preview}
        </div>
      </div>
    </div>
  );
}
